import { ConfigError } from "../config/error";
import { type Resolver } from "../config/resolver";
import { ensureError } from "../error";
import { type Plugin } from "../plugin";
import { isThenable } from "../utils/is-thenable";
import { getTransformerFromModule } from "./get-transformer-from-module";
import { getTransformerFromPlugins } from "./get-transformer-from-plugins";
import { type Transformer } from "./transformer";
import { TRANSFORMER_API } from "./transformer-api";

function validateTransformer(transformer: Transformer): void {
	/* istanbul ignore next */
	const version = transformer.api ?? 0;

	/* check if transformer version is supported */
	if (version !== TRANSFORMER_API.VERSION) {
		throw new ConfigError(
			`Transformer uses API version ${String(version)} but only version ${String(TRANSFORMER_API.VERSION)} is supported`,
		);
	}
}

function loadTransformerFunction(
	resolvers: Resolver[],
	name: string,
	plugins: Plugin[],
): Transformer | Promise<Transformer> {
	/* try to match a transformer (named or unnamed) from plugin */
	const transformer = getTransformerFromPlugins(name, plugins);
	if (transformer) {
		return transformer;
	}

	/* assume transformer refers to a regular module */
	return getTransformerFromModule(resolvers, name);
}

/**
 * Get transformation function requested by configuration.
 *
 * Searches:
 *
 * - Named transformers from plugins.
 * - Unnamed transformer from plugin.
 * - Standalone modules (local or node_modules)
 *
 * @internal
 * @param name - Key from configuration
 */
export function getTransformerFunction(
	resolvers: Resolver[],
	name: string,
	plugins: Plugin[],
): Transformer | Promise<Transformer> {
	try {
		const transformer = loadTransformerFunction(resolvers, name, plugins);
		if (isThenable(transformer)) {
			/* eslint-disable-next-line unicorn/prefer-await -- intentional, we must return sync result if sync parameters are used */
			return transformer.then((transformer) => {
				validateTransformer(transformer);
				return transformer;
			});
		}
		validateTransformer(transformer);
		return transformer;
	} catch (err: unknown) {
		if (err instanceof ConfigError) {
			throw new ConfigError(`Failed to load transformer "${name}": ${err.message}`, err);
		}
		throw new ConfigError(`Failed to load transformer "${name}"`, ensureError(err));
	}
}

/**
 * Cached version of [[getTransformerFunction]].
 *
 * @internal
 */
/* istanbul ignore next: only testing non-cached version */
export function getCachedTransformerFunction(
	cache: Map<string, Transformer>,
	resolvers: Resolver[],
	name: string,
	plugins: Plugin[],
): Transformer | Promise<Transformer> {
	const cached = cache.get(name);
	if (cached) {
		return cached;
	}
	const transformer = getTransformerFunction(resolvers, name, plugins);
	if (isThenable(transformer)) {
		/* eslint-disable-next-line unicorn/prefer-await -- intentional, we must return sync result if sync parameters are used */
		return transformer.then((transformer) => {
			cache.set(name, transformer);
			return transformer;
		});
	}
	cache.set(name, transformer);
	return transformer;
}
